import PropTypes from 'prop-types';
import { Flower2, Sun } from 'lucide-react';

/**
 * ViewPanelButtons - Einstieg der View-Panels im 2D-Editor (#633)
 *
 * EIN zentriertes Button-Paar unten mittig für Blüte (#608) und
 * Sonne & Schatten (#605) — ersetzt die zwei separat versetzten Einzel-Buttons.
 * Die Panels selbst rendert der Aufrufer: Desktop in der rechten Seitenleiste,
 * Tablet/Mobile im FloatingViewPanel. Gegenseitige Exklusivität steuert der
 * activeViewPanel-State in MainLayoutContent.
 *
 * Canvas-Verdeckung (#650): Der Container ist `pointer-events-none`, nur die
 * Buttons selbst fangen Klicks ab — die Lücken zwischen/um sie lassen Klicks
 * zum Canvas durch. Zusätzlich blendet `hidden` das Paar dezent aus (transparent
 * + click-through), solange aktiv editiert wird, damit es die Zeichenfläche
 * unten mittig nicht blockiert.
 */

const BUTTONS = [
  { id: 'bloom', label: 'Blüte', ariaLabel: 'Blüte', Icon: Flower2, activeClasses: 'border-pink-300 bg-pink-50 text-pink-700' },
  { id: 'sun', label: 'Sonne & Schatten', ariaLabel: 'Sonne und Schatten', Icon: Sun, activeClasses: 'border-emerald-300 bg-emerald-50 text-emerald-700' },
];

const ViewPanelButtons = ({ activePanel, onToggle, hidden }) => (
  <div
    className={`pointer-events-none fixed bottom-4 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 transition-opacity duration-200 ${
      hidden ? 'opacity-0' : 'opacity-100'
    }`}
    aria-hidden={hidden || undefined}
  >
    {BUTTONS.map(({ id, label, ariaLabel, Icon, activeClasses }) => {
      const active = activePanel === id;
      return (
        <button
          key={id}
          type="button"
          aria-pressed={active}
          aria-label={ariaLabel}
          tabIndex={hidden ? -1 : undefined}
          onClick={() => onToggle(id)}
          className={`inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium shadow-sm transition-colors ${
            hidden ? 'pointer-events-none' : 'pointer-events-auto'
          } ${active ? activeClasses : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
          {label}
        </button>
      );
    })}
  </div>
);

ViewPanelButtons.propTypes = {
  activePanel: PropTypes.oneOf(['bloom', 'sun']),
  onToggle: PropTypes.func.isRequired,
  // Dezent ausblenden + click-through, während aktiv editiert wird (#650)
  hidden: PropTypes.bool,
};

ViewPanelButtons.defaultProps = {
  activePanel: null,
  hidden: false,
};

export default ViewPanelButtons;
